import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { StudyQuery } from '../../data/DataSource';
import { MODALITY_CODES } from '../../features/upload/modalities';
import { listCollections, listTags } from '../../features/collections/api';
import { useSelectedWorkspace } from '../../stores/workspaceStore';

interface Props {
  initialPatient?: string;
  initialModality?: string;
  initialCollection?: string;
  initialTag?: string;
  onChange: (query: StudyQuery) => void;
}

const ANY = '__any__';
// One list shared with the upload form.
const MODALITIES = MODALITY_CODES;

export function WorklistFilters({
  initialPatient, initialModality, initialCollection, initialTag, onChange,
}: Props) {
  const [patient, setPatient] = useState(initialPatient ?? '');
  const [modality, setModality] = useState<string>(initialModality || ANY);
  const [collection, setCollection] = useState<string>(initialCollection || ANY);
  const [tag, setTag] = useState<string>(initialTag || ANY);

  // Both lists belong to the workspace the header selected, so they are keyed on
  // it — switching workspace must not offer another tenant's collection names.
  const { current, ready } = useSelectedWorkspace();
  const workspace = current?.public_id;
  const { data: cols } = useQuery({
    queryKey: ['collections', workspace],
    queryFn: () => listCollections(workspace),
    enabled: ready,
  });
  const { data: tags } = useQuery({
    queryKey: ['tags', workspace],
    queryFn: () => listTags(workspace),
    enabled: ready,
  });

  const apply = (next: { mod?: string; col?: string; tg?: string } = {}) =>
    onChange({
      patientName: patient.trim() || undefined,
      modality: (next.mod ?? modality) === ANY ? undefined : (next.mod ?? modality),
      collection: (next.col ?? collection) === ANY ? undefined : (next.col ?? collection),
      tags: (next.tg ?? tag) === ANY ? undefined : [next.tg ?? tag],
    });

  const reset = () => {
    setPatient('');
    setModality(ANY);
    setCollection(ANY);
    setTag(ANY);
    onChange({});
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">Patient name / ID</label>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search…"
            value={patient}
            onChange={(e) => setPatient(e.currentTarget.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
            className="w-60 pl-8"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">Modality</label>
        <Select
          value={modality}
          onValueChange={(v) => {
            setModality(v);
            apply({ mod: v });
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            {MODALITIES.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(cols?.collections.length ?? 0) > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Collection</label>
          <Select
            value={collection}
            onValueChange={(v) => { setCollection(v); apply({ col: v }); }}
          >
            <SelectTrigger className="w-44"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any</SelectItem>
              {cols?.collections.map((c) => (
                <SelectItem key={c.public_id} value={c.public_id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {(tags?.tags.length ?? 0) > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Tag</label>
          <Select value={tag} onValueChange={(v) => { setTag(v); apply({ tg: v }); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any</SelectItem>
              {tags?.tags.map((t) => (
                <SelectItem key={t.public_id} value={t.public_id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button onClick={() => apply()}>Search</Button>
      <Button variant="ghost" onClick={reset}>
        Reset
      </Button>
    </div>
  );
}
